import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type CSSProperties, cloneElement, type ReactElement, type ReactNode, type Ref } from 'react';
import { cn } from '../../../styles/lib/utils';

type DraggableChildProps = {
  ref?: Ref<Element>;
  style?: CSSProperties;
  className?: string;
  dragHandleProps?: object;
  isDragging?: boolean;
  [key: string]: unknown;
};

type DraggableProps = {
  id: string;
  children: ReactNode;
  provideDragHandleProps?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
  className?: string;
  dragOpacity?: number;
  transition?: string;
  dragZIndex?: number;
  asChild?: boolean;
  isDragOverlay?: boolean;
};

/**
 * Draggable component that allows items to be dragged and reordered.
 * When asChild is true, merges props with the child element instead of wrapping it.
 */
function Draggable({
  id,
  children,
  provideDragHandleProps = false,
  disabled = false,
  style: customStyle,
  className,
  dragOpacity = 0.8,
  transition = 'width transform 0.2s ease-in-out',
  dragZIndex = 3,
  asChild = false,
  isDragOverlay = false,
}: DraggableProps) {
  const {
    attributes,
    isDragging,
    isOver,
    listeners,
    setNodeRef,
    transform,
    transition: sortableTransition,
  } = useSortable({
    id,
    disabled,
    transition: {
      duration: 200,
      easing: 'ease-out',
    },
  });

  // Preserve existing position (especially sticky) when dragging to prevent layout shifts
  const existingPosition = customStyle?.position;
  const shouldPreservePosition =
    existingPosition === 'sticky' || existingPosition === 'fixed' || existingPosition === 'absolute';

  // When using DragOverlay (isDragOverlay=true), the source element should remain visible in place
  // but still show transform preview. When not using DragOverlay, use the original behavior.
  // Use sortableTransition when not dragging (for reordering animations), custom transition when dragging
  const style: CSSProperties = isDragOverlay
    ? {
        ...customStyle,
        // Keep source visible and show transform preview when dragging (DragOverlay handles the cursor-following visual)
        opacity: isDragging && !isOver ? 0.5 : 1,
        transform: CSS.Translate.toString(transform),
        // Use sortable transition for smooth reordering animations when not dragging
        transition: isDragging ? transition : sortableTransition || transition,
      }
    : {
        ...customStyle,
        opacity: isDragging ? dragOpacity : 1,
        // Only set position to relative if there's no existing position or it's not sticky/fixed/absolute
        // This preserves sticky positioning during drag to prevent layout shifts
        ...(isDragging && !shouldPreservePosition && { position: 'relative' }),
        transform: CSS.Translate.toString(transform),
        // Use sortable transition for smooth reordering animations when not dragging
        transition: isDragging ? transition : sortableTransition || transition,
        ...(isDragging && { zIndex: dragZIndex }),
      };

  // If asChild is true, merge props with child element
  if (asChild) {
    const child = children as ReactElement<DraggableChildProps>;
    // If we need to provide drag handle props to children, merge them
    if (provideDragHandleProps) {
      return cloneElement(child, {
        ref: setNodeRef,
        style: { ...child.props.style, ...style },
        className: cn(className, child.props.className),
        dragHandleProps: { ...attributes, ...listeners },
        ...(isDragging && { isDragging }),
      });
    }
    // Otherwise, merge drag handlers directly
    return cloneElement(child, {
      ref: setNodeRef,
      style: { ...child.props.style, ...style },
      className: cn(className, child.props.className),
      ...(isDragging && { isDragging }),
      ...attributes,
      ...listeners,
    });
  }

  // If we need to provide drag handle props to children, clone the element
  if (provideDragHandleProps) {
    return (
      <div ref={setNodeRef} style={style} className={className}>
        {cloneElement(children as ReactElement<DraggableChildProps>, {
          dragHandleProps: { ...attributes, ...listeners },
          ...(isDragging && { isDragging }),
        })}
      </div>
    );
  }

  // Otherwise, apply drag handlers to the wrapper
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={className}
      {...(isDragging && { isDragging })}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

export { Draggable, type DraggableProps };
